/**
 * Pritect Beacon — Consent Script Entry Point
 *
 * Usage:
 * <script
 *   src="https://ynbeopknlxoayzdocsmu.supabase.co/storage/v1/object/public/consent-scripts/v1/pritect-beacon.min.js"
 *   data-domain-id="YOUR_DOMAIN_ID"
 *   data-api-url="https://your-project.supabase.co"
 *   data-anon-key="YOUR_SUPABASE_ANON_KEY"
 * ></script>
 */

import { ConsentEngine } from "./core/ConsentEngine";
import { GoogleConsentMode } from "./gcm/GoogleConsentMode";
import { getScriptConfig, installTcfStub } from "./scriptConfig";

const SUPABASE_URL = "https://ynbeopknlxoayzdocsmu.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_vU9Z7i0RWBnGJtu3p7_t5Q_JgZuhJBP";
let engine: ConsentEngine | null = null;
let pendingPreferencesOpen = false;

async function boot(): Promise<void> {
  const config = getScriptConfig();
  if (!config) return;

  // Synchronous consent-signalling setup, before any await, so it lands before
  // gtag / TCF vendor tags execute. Opt-in via data-gcm / data-tcf on the embed.
  if (config.gcm) {
    try { new GoogleConsentMode(true).setDefaults(); } catch { /* no-op */ }
  }
  if (config.tcf) {
    try { installTcfStub(); } catch { /* no-op */ }
  }

  engine = new ConsentEngine({
    domainId: config.domainId,
    apiUrl: (config.apiUrl ?? SUPABASE_URL).replace(/\/$/, ""),
    anonKey: config.anonKey ?? SUPABASE_ANON_KEY,
    lang: config.lang,
  });

  await engine.init();
  if (pendingPreferencesOpen) {
    pendingPreferencesOpen = false;
    engine.showPreferences();
  }
}

// Expose public API on window for custom integrations
declare global {
  interface Window {
    PritectBeacon: {
      showPreferences: () => void;
      revokeConsent: () => void;
    };
  }
}

// Placeholder public API — full implementation wired through ConsentEngine
window.PritectBeacon = {
  showPreferences: () => {
    if (engine) {
      engine.showPreferences();
      return;
    }
    pendingPreferencesOpen = true;
  },
  revokeConsent: () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("pb_")) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    // Also clear the pb_consent cookie
    document.cookie = "pb_consent=; max-age=0; path=/; SameSite=Lax";
    window.location.reload();
  },
};

// Boot immediately so script interception starts before later tags execute.
void boot().catch((error) => {
  console.warn("[Pritect Beacon] Failed to initialize.", error);
  window.dispatchEvent(new CustomEvent("pb:error", { detail: { message: "Failed to initialize consent manager." } }));
});
